"use client";

import { useState } from "react";

const BRAND_RED = "#ef4136";

const VENN = [
  { label: "Client expectations", short: ["Client", "expectations"], body: "What the client wants, needs, and is measured on — the brief behind the brief." },
  { label: "Market realities", short: ["Market", "realities"], body: "What the market will actually reward: the category, the competition, the audience's habits." },
  { label: "Best practices", short: ["Best", "practices"], body: "What we know works — craft, accessibility, and the practical realities of building it." }
];

const CIRCLES = [
  { cx: 374.5, cy: 242.5, labelLeft: "50%", labelTop: "14%" },
  { cx: 242.5, cy: 479.5, labelLeft: "22%", labelTop: "68%" },
  { cx: 507.5, cy: 479.5, labelLeft: "78%", labelTop: "68%" }
];

export default function VennDiagram() {
  const [active, setActive] = useState(null);
  const [hovered, setHovered] = useState(null);
  const shown = hovered !== null ? hovered : active;

  const pick = (i) => setActive((cur) => (cur === i ? null : i));

  return (
    <div
      className="px-sec px-grid-stack"
      style={{ paddingTop: 88, paddingBottom: 88, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}
    >
      <div>
        <h2 style={{ margin: "0 0 32px", fontSize: "clamp(30px, 3.4vw, 46px)", letterSpacing: "-0.025em", fontWeight: 400 }}>How we think</h2>
        <div style={{ fontSize: "clamp(21px, 2.3vw, 28px)", lineHeight: 1.45, letterSpacing: "-0.014em", marginBottom: 30 }}>
          Every project is different.<br />Creativity is subjective.<br />Nobody has all the answers.
        </div>
        <p style={{ margin: "0 0 40px", fontSize: 17, lineHeight: 1.65, color: "var(--px-text-secondary)", maxWidth: 540 }}>
          That’s why we don’t create in a vacuum. At Pixels&amp;, we balance client expectations with market realities and best practices to make sure we create work that truly delivers.
        </p>
        <div style={{ display: "flex", flexDirection: "column", borderTop: "1px solid var(--px-border)", maxWidth: 540 }}>
          {VENN.map((v, i) => {
            const on = shown === i;
            return (
              <button
                key={v.label}
                type="button"
                onClick={() => pick(i)}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(i)}
                onBlur={() => setHovered(null)}
                aria-pressed={active === i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 20,
                  padding: "18px 14px 18px 0",
                  textAlign: "left",
                  border: "none",
                  borderBottom: "1px solid var(--px-border)",
                  background: on ? "var(--px-bg)" : "none",
                  cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: on ? "var(--px-text)" : "var(--px-text-tertiary)",
                  transition: "background 0.25s, color 0.25s"
                }}
              >
                <span style={{ paddingLeft: on ? 14 : 0, transition: "padding-left 0.25s ease" }}>{v.label}</span>
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-block",
                    width: 6,
                    height: 6,
                    flex: "0 0 auto",
                    borderRadius: "50%",
                    background: BRAND_RED,
                    opacity: on ? 1 : 0,
                    transform: on ? "translateX(0)" : "translateX(8px)",
                    transition: "transform 0.25s ease, opacity 0.2s ease"
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <div style={{ position: "relative", width: "100%", maxWidth: 560, margin: "0 auto" }}>
          <svg viewBox="0 0 750 722" style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }} role="img" aria-label="How we think: client expectations, market realities and best practices">
            {CIRCLES.map((c, i) => {
              const on = shown === i;
              const dim = shown !== null && shown !== i;
              return (
                <circle
                  key={i}
                  cx={c.cx}
                  cy={c.cy}
                  r="242"
                  vectorEffect="non-scaling-stroke"
                  onClick={() => pick(i)}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    cursor: "pointer",
                    fill: on ? "rgba(239,65,54,0.07)" : "transparent",
                    stroke: on ? BRAND_RED : dim ? "var(--px-text-faint)" : "var(--px-text)",
                    strokeWidth: on ? 2 : 1,
                    transition: "fill 0.3s, stroke 0.3s, stroke-width 0.3s"
                  }}
                />
              );
            })}
          </svg>
          {CIRCLES.map((c, i) => {
            const dim = shown !== null && shown !== i;
            return (
              <div key={i} style={{ position: "absolute", left: c.labelLeft, top: c.labelTop, transform: "translateX(-50%)", textAlign: "center", whiteSpace: "nowrap", pointerEvents: "none" }}>
                <span style={{ display: "inline-block", fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 12, lineHeight: 1.5, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 500, color: dim ? "var(--px-text-faint)" : "var(--px-text)", transition: "color 0.3s" }}>
                  {VENN[i].short[0]}<br />{VENN[i].short[1]}
                </span>
              </div>
            );
          })}
          <div style={{ position: "absolute", left: "50%", top: "53%", transform: "translate(-50%, -50%)", textAlign: "center", whiteSpace: "nowrap", pointerEvents: "none" }}>
            <span style={{ display: "inline-block", fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 12, lineHeight: 1.5, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 500, color: "var(--px-text)", opacity: shown === null ? 1 : 0.35, transition: "opacity 0.3s" }}>
              Work that<br />delivers
            </span>
          </div>
        </div>
        <p style={{ margin: "26px auto 0", minHeight: 56, maxWidth: 480, textAlign: "center", fontSize: 17, lineHeight: 1.6, color: shown === null ? "var(--px-text-tertiary)" : "var(--px-text)", transition: "color 0.3s" }}>
          {shown === null ? "Select one to see how it shapes the work. The overlap is where we build." : VENN[shown].body}
        </p>
      </div>
    </div>
  );
}
